<template>
  <div class="relative h-full w-full text-center">
    <template v-for="(image, index) in images" :key="image.src">
      <RevButtonBase
        v-if="!image.isExtra"
        v-bind="{
          ...(activeIndex !== index && {
            'aria-hidden': true,
            tabindex: -1,
          }),
        }"
        class="mx-auto block md:w-full"
        :class="{
          'focus-visible-outline-inset-hi cursor-pointer': allowMediaViewer,
        }"
        @click="emit('click', image, index)"
      >
        <MainImage
          :active-index
          :class="{
            'absolute inset-0 hidden md:block': index !== 0,
            'invisible opacity-0 md:z-[-1]': activeIndex !== index,
          }"
          :decoding="index === 0 ? 'sync' : 'async'"
          :fetchpriority="index === 0 ? 'high' : 'low'"
          :hidden="activeIndex !== index"
          :image
          :index
          :is-mobile
          :loading="index === 0 ? 'eager' : 'lazy'"
          @visible="emit('visible', index)"
        />
        <!-- We use a div with a button role as we can not nest two real buttons -->
        <div
          v-if="withSticker && index === 0"
          class="absolute bottom-8 left-8 md:bottom-auto md:left-auto md:right-8 md:top-8"
          role="button"
          tabindex="0"
          @click="handleStickerClick"
          @keypress.enter="handleStickerClick"
        >
          <Sticker />
        </div>
      </RevButtonBase>
      <div
        v-else-if="image.isExtra && image.type === 'review' && review"
        v-show="activeIndex === index"
        class="mx-auto hidden md:block"
      >
        <Review
          ref="reviewCard"
          :card-class-name="BIG_ITEM_SIZE_CLASS"
          :class="{
            'absolute inset-0': activeIndex === index,
            'invisible opacity-0 md:z-[-1]': activeIndex !== index,
          }"
          is-in-main-container
          :review
          @click="emit('extra-item-click', 'review')"
          @visible="emit('visible', index, 'review')"
        />
      </div>
      <div
        v-else-if="image.isExtra && image.type === 'video'"
        v-show="activeIndex === index"
        class="mx-auto hidden md:block"
      >
        <div
          :class="{
            'absolute inset-0': activeIndex === index,
            'invisible opacity-0 md:z-[-1]': activeIndex !== index,
          }"
        >
          <YoutubeVideo
            ref="videoCard"
            :is-mobile
            @click="emit('extra-item-click', 'video')"
            @start="emit('visible', index, 'video')"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'

import type { Review as ReviewType } from '@backmarket/nuxt-layer-cms/ReviewsView.vue'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { openModal } from '@ds/components/ModalBase'

import { MODAL_NAMES } from '~/scopes/product/constants'

import { BIG_ITEM_SIZE_CLASS } from '../GalleryNew.constants'
import type {
  GalleryImage,
  GalleryImageExtraItemType,
} from '../GalleryNew.types'

import MainImage from './MainImage.vue'
import Review from './Review/Review.vue'
import Sticker from './Sticker/Sticker.vue'
import YoutubeVideo from './YoutubeVideo/YoutubeVideo.vue'

const {
  images,
  activeIndex = 0,
  review,
  allowMediaViewer,
  isMobile,
} = defineProps<{
  images: GalleryImage[]
  review?: ReviewType | null
  activeIndex?: number
  allowMediaViewer: boolean
  // Only used for tracking and for resetting the active index, not for layout
  isMobile: boolean
  withSticker?: boolean
}>()

const emit = defineEmits<{
  (e: 'click', image: GalleryImage, index: number): void
  (e: 'extra-item-click', imageType: GalleryImageExtraItemType): void
  (e: 'visible', index: number, imageType?: GalleryImageExtraItemType): void
}>()

const reviewCard = useTemplateRef('reviewCard')
const videoCard = useTemplateRef('videoCard')

defineExpose({
  reviewCard,
  videoCard,
})

function handleStickerClick(e: Event) {
  e.stopPropagation()
  e.preventDefault()

  openModal(MODAL_NAMES.VERIFIED_REFURBISHED)
}
</script>
